

import React, { useMemo } from 'react';
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Cell, Tooltip } from 'recharts';
import { ChordValidation } from '../types'; // Axe 2: Import ChordValidation

interface ChromaVisualizerProps {
  data: number[][]; // Historique des spectres de référence (Master)
  studentData?: number[][]; // Historique des spectres de l'élève
  validation?: ChordValidation[]; // Axe 2: Use ChordValidation instead of any
  lang?: 'en' | 'fr';
  activeMeasure: number;
}

const NOTES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

export const ChromaVisualizer: React.FC<ChromaVisualizerProps> = ({ data: refChromaHistory, studentData, validation = [], activeMeasure }) => {
  const v = validation.find(val => val.measureIndex === activeMeasure);
  
  const displayData = useMemo(() => {
    // 1. Extraction de la fenêtre Master (Réel)
    const framesPerBarRef = (refChromaHistory?.length || 0) / Math.max(1, validation.length);
    const startIdxRef = Math.floor(activeMeasure * framesPerBarRef);
    const endIdxRef = Math.floor((activeMeasure + 1) * framesPerBarRef);
    
    const avgRef = new Array(12).fill(0);
    let countRef = 0;
    if (refChromaHistory && refChromaHistory.length > 0) {
        for (let i = startIdxRef; i < endIdxRef; i++) {
            if (refChromaHistory[i]) {
                refChromaHistory[i].forEach((val, k) => avgRef[k] += val);
                countRef++;
            }
        }
    }

    // 2. Extraction de la fenêtre Elève (Réel)
    const framesPerBarStu = (studentData?.length || 0) / Math.max(1, validation.length);
    const startIdxStu = Math.floor(activeMeasure * framesPerBarStu);
    const endIdxStu = Math.floor((activeMeasure + 1) * framesPerBarStu);
    
    const avgStu = new Array(12).fill(0);
    let countStu = 0;
    if (studentData && studentData.length > 0) {
        for (let i = startIdxStu; i < endIdxStu; i++) {
            if (studentData[i]) {
                studentData[i].forEach((val, k) => avgStu[k] += val);
                countStu++;
            }
        }
    }

    return NOTES.map((note, i) => {
        const valStu = countStu > 0 ? (avgStu[i] / countStu) * 100 : 0;
        const valRef = countRef > 0 ? (avgRef[i] / countRef) * 100 : 0;
        return {
            note,
            val: Math.round(valStu),
            target: Math.round(valRef)
        };
    });
  }, [refChromaHistory, studentData, activeMeasure, validation]);

  const hasData = displayData.some(d => d.val > 5 || d.target > 5);

  return (
    <div className="bg-[#02080a]/60 p-8 md:p-12 rounded-[56px] border-2 border-white/10 shadow-3xl h-full flex flex-col gap-8 backdrop-blur-3xl relative overflow-hidden">
      <div className="flex justify-between items-start z-10">
        <div className="space-y-2">
            <h3 className="text-3xl font-black text-white italic uppercase tracking-tighter">Anatomie <span className="text-smart-accent">Spectrale</span></h3>
            <p className="text-[10px] text-slate-500 font-black uppercase tracking-[0.4em]">
                Comparaison fréquentielle réelle - Mesure <span className="text-white">{activeMeasure + 1}</span>
            </p>
        </div>
        <div className="flex gap-4">
            <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-white/10 rounded-sm"></div>
                <span className="text-[8px] font-black text-slate-500 uppercase">Empreinte Master</span>
            </div>
            <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-smart-accent rounded-sm"></div>
                <span className="text-[8px] font-black text-smart-accent uppercase">Votre Spectre</span>
            </div>
        </div>
      </div>

      <div className="flex-1 bg-black/40 rounded-[40px] p-6 border border-white/5 relative min-h-[350px] shadow-inner overflow-hidden">
          {!hasData ? (
              <div className="flex flex-col items-center justify-center h-full opacity-20">
                  <span className="text-6xl mb-4">🎸</span>
                  <p className="text-[10px] font-black uppercase tracking-[0.4em]">En attente de signal réel...</p>
              </div>
          ) : (
              <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={displayData} margin={{ top: 20, bottom: 0 }}>
                      <XAxis dataKey="note" axisLine={false} tickLine={false} tick={{fill: '#475569', fontSize: 12, fontWeight: 900}} />
                      <YAxis hide domain={[0, 110]} />
                      <Tooltip cursor={{fill: 'transparent'}} content={() => null} />
                      
                      {/* MASTER REAL SPECTRUM (GHOST) */}
                      <Bar dataKey="target" fill="rgba(255,255,255,0.08)" radius={[8,8,0,0]} barSize={40} isAnimationActive={false} />
                      
                      {/* STUDENT REAL SPECTRUM */}
                      <Bar dataKey="val" radius={[8,8,0,0]} barSize={20} style={{ transform: 'translateX(-10px)' }}>
                          {displayData.map((entry, index) => {
                              // Couleur d'alerte si déviation spectrale forte
                              const isDeviant = entry.val > entry.target + 30 && entry.target < 10;
                              return (
                                <Cell key={`cell-${index}`} fill={isDeviant ? '#ef4444' : '#FF6261'} fillOpacity={entry.val > 10 ? 0.9 : 0.2} />
                              );
                          })}
                      </Bar>
                  </BarChart>
              </ResponsiveContainer>
          )}
      </div>

      <div className={`p-6 border rounded-[32px] z-10 ${v?.isCorrect ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-red-500/5 border-red-500/20'}`}>
          <p className="text-xs text-slate-300 italic leading-relaxed font-medium">
              {v?.isCorrect 
                ? "L'empreinte spectrale confirme que vous jouez les mêmes harmoniques que le Master. Justesse validée." 
                : `Divergence spectrale détectée. Les fréquences rouges indiquent des notes hors-grille ou un défaut de muting sur les cordes adjacentes.`}
          </p>
      </div>
    </div>
  );
};